import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { UnoCardView, UnoCardBack } from "@/components/UnoCard";
import InviteFriends from "@/components/InviteFriends";
import DealAnimation, { WinAnimation, PlayFlightLayer } from "@/components/GameAnimations";
import QuickChat, { ReactionBubble, useReactions } from "@/components/QuickChat";
import { useAuth } from "@/context/AuthContext";
import { GAME } from "@/constants/testIds";

const COLOR_HEX = { red: "#FF3366", yellow: "#FFD600", green: "#00E676", blue: "#00BFFF" };
const RULE_LABELS = { stack_draws: "Stacking", draw_to_match: "Draw to match", force_play: "Force play" };

function canPlayCardClient(card, topCard, currentColor, pendingDraw, pendingType, rules) {
  const stackOn = rules?.stack_draws ?? true;
  if (pendingDraw > 0 && stackOn) {
    if (pendingType === "draw2") return card.value === "draw2";
    if (pendingType === "wild4") return card.value === "wild4";
  }
  if (card.color === "wild") return true;
  if (card.color === currentColor) return true;
  if (topCard && card.value === topCard.value && topCard.color !== "wild") return true;
  return false;
}

export default function UnoGame({ state, send, lastReaction }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const skin = user?.active_skin || "classic";
  const [chatOpen, setChatOpen] = useState(false);
  const [chatText, setChatText] = useState("");
  const [pickingColorFor, setPickingColorFor] = useState(null);
  const chatEndRef = useRef(null);

  const chat = state?.chat || [];

  // Toast on last-action penalty for the current user (forgot to call)
  const lastActionRef = useRef(null);
  const [playFlights, setPlayFlights] = useState([]);
  useEffect(() => {
    const la = state?.last_action;
    if (!la) return;
    const sig = JSON.stringify(la);
    if (sig === lastActionRef.current) return;
    lastActionRef.current = sig;
    if (la.type === "play" && la.penalty_drew > 0 && la.user_id === user?.user_id) {
      toast.error("Forgot to Call! +1 penalty card");
    }
    // Enqueue a play flight animation for any play (including our own)
    if (la.type === "play" && la.card) {
      const id = Math.random().toString(36).slice(2);
      setPlayFlights(prev => [...prev, { id, card: la.card, fromUserId: la.user_id, variant: "uno" }]);
    }
  }, [state?.last_action, user?.user_id]);

  const dequeueFlight = (id) => setPlayFlights(prev => prev.filter(p => p.id !== id));

  // Quick chat reactions (received from GameHub via `lastReaction` prop)
  const [reactions, pushReaction] = useReactions();
  useEffect(() => { if (lastReaction) pushReaction(lastReaction); }, [lastReaction, pushReaction]);

  // Typed chat messages also bubble above the sender's avatar for ~4 seconds
  const chatSeenRef = useRef(0);
  useEffect(() => {
    if (!chat || chat.length === 0) { chatSeenRef.current = chat?.length || 0; return; }
    if (chat.length <= chatSeenRef.current) return;
    const fresh = chat.slice(chatSeenRef.current);
    chatSeenRef.current = chat.length;
    for (const m of fresh) {
      if (!m || !m.user_id) continue;
      pushReaction({
        user_id: m.user_id,
        emoji: "💬",
        text: (m.text || "").slice(0, 60),
      });
    }
  }, [chat, pushReaction]);

  useEffect(() => {
    if (chatOpen && chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [chat, chatOpen]);

  // Deal animation: trigger once when status transitions from "waiting" -> "playing"
  const [dealActive, setDealActive] = useState(false);
  const prevStatusRef = useRef(state?.status);
  useEffect(() => {
    if (prevStatusRef.current === "waiting" && state?.status === "playing") {
      setDealActive(true);
    }
    prevStatusRef.current = state?.status;
  }, [state?.status]);

  // Draw pulse: highlight a player's avatar for a moment when their hand grows
  const prevHandRef = useRef({});
  const [drawPulse, setDrawPulse] = useState({});
  useEffect(() => {
    if (!state?.players) return;
    const nextPulse = {};
    for (const p of state.players) {
      const prev = prevHandRef.current[p.user_id];
      if (prev !== undefined && p.hand_count > prev) {
        nextPulse[p.user_id] = Date.now();
      }
      prevHandRef.current[p.user_id] = p.hand_count;
    }
    if (Object.keys(nextPulse).length) {
      setDrawPulse(prev => ({ ...prev, ...nextPulse }));
      setTimeout(() => {
        setDrawPulse(prev => {
          const now = Date.now();
          const filtered = {};
          for (const [k, v] of Object.entries(prev)) if (now - v < 900) filtered[k] = v;
          return filtered;
        });
      }, 1000);
    }
  }, [state?.players]);

  const me = useMemo(() => state?.players.find(p => p.user_id === user?.user_id), [state, user]);
  const myIdx = useMemo(() => state ? state.players.findIndex(p => p.user_id === user?.user_id) : -1, [state, user]);
  const isMyTurn = state && myIdx >= 0 && state.current_player_idx === myIdx && state.status === "playing";
  const isHost = state && state.host_id === user?.user_id;
  const topCard = state?.top_card;
  const pendingSwap = state?.pending_swap;
  const iAmSwapChooser = pendingSwap && state.players[pendingSwap.from_idx]?.user_id === user?.user_id;
  const rules = state?.rules || {};
  const currentPlayer = state?.players[state?.current_player_idx];

  const playCard = (card) => {
    if (!isMyTurn) return;
    if (!canPlayCardClient(card, topCard, state.current_color, state.pending_draw, state.pending_type, rules)) {
      toast.error("Can't play that card");
      return;
    }
    if (card.color === "wild") {
      setPickingColorFor(card);
      return;
    }
    send({ type: "play", card_id: card.id });
  };

  const chooseColor = (color) => {
    if (!pickingColorFor) return;
    send({ type: "play", card_id: pickingColorFor.id, chosen_color: color });
    setPickingColorFor(null);
  };

  const drawCard = () => { if (isMyTurn) send({ type: "draw" }); };
  const callUno = () => { send({ type: "call_uno" }); };
  const startGame = () => { send({ type: "start" }); };
  const addBot = () => { send({ type: "add_bot" }); };
  const restart = () => { send({ type: "restart" }); };
  const chooseSwapTarget = (targetUserId) => { send({ type: "swap_target", target_user_id: targetUserId }); };
  const sendChat = () => {
    if (!chatText.trim()) return;
    send({ type: "chat", text: chatText });
    setChatText("");
  };

  const opponents = state.players.filter(p => p.user_id !== user?.user_id);
  const modeLabel = state.mode === "blitz" ? "Blitz" : state.mode === "seven_zero" ? "Seven-Zero" : "Classic";
  const activeRules = Object.entries(rules).filter(([, v]) => v).map(([k]) => RULE_LABELS[k]).filter(Boolean);

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" data-testid={GAME.container}>
      {/* Minimal top bar */}
      <div className="flex items-center justify-between gap-2 px-3 sm:px-6 py-2 sm:py-3 border-b border-white/5 glass shrink-0">
        <button onClick={() => nav("/lobby", { replace: true })} data-testid={GAME.leaveBtn}
          className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white flex items-center gap-1">
          <span>←</span> <span className="hidden sm:inline">Leave</span>
        </button>
        <div className="flex items-center gap-2">
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/40 hidden sm:inline">Room</span>
          <button
            data-testid={GAME.copyRoomCodeBtn}
            onClick={() => { navigator.clipboard?.writeText(state.code); toast.success("Code copied"); }}
            className="font-display text-sm sm:text-lg tracking-[0.2em] sm:tracking-[0.3em] px-2 sm:px-3 py-1 rounded-md bg-white/5 border border-white/10 hover:bg-white/10"
          >{state.code}</button>
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-white/60 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 hidden sm:inline">{modeLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Chat + React buttons moved to floating dock at bottom-right */}
        </div>
      </div>

      {/* Waiting room */}
      {state.status === "waiting" && (
        <div className="flex-1 flex items-start justify-center p-4 sm:p-6 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-w-5xl w-full">
            <div className="glass rounded-2xl p-6 sm:p-8 lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.3em] text-white/50">Waiting room · {modeLabel}</div>
              <div className="font-display text-2xl sm:text-3xl mt-2">Share code <span style={{color:"var(--uno-yellow)"}}>{state.code}</span></div>
              <p className="text-white/60 mt-2 text-sm sm:text-base">Need at least 2 players. Invite friends or add bots to fill.</p>
              {activeRules.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {activeRules.map(r => (
                    <span key={r} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[color:var(--uno-green)]/15 border border-[color:var(--uno-green)]/40 text-[color:var(--uno-green)]">{r}</span>
                  ))}
                </div>
              )}
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {state.players.map(p => (
                  <div key={p.user_id} className="rounded-xl border border-white/10 p-3 flex flex-col items-center bg-black/20">
                    <img src={p.avatar} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-white/20"/>
                    <div className="mt-2 text-xs sm:text-sm font-semibold">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50">{p.is_bot ? "Bot" : (p.user_id === state.host_id ? "Host" : "Player")}</div>
                  </div>
                ))}
                {Array.from({ length: state.max_players - state.players.length }).map((_,i) => (
                  <div key={"empty"+i} className="rounded-xl border border-dashed border-white/10 p-3 flex items-center justify-center text-white/30 min-h-[92px]">Empty</div>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                {isHost && state.players.length < state.max_players && (
                  <button data-testid={GAME.addBotBtn} onClick={addBot} className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold">+ Add bot</button>
                )}
                {isHost && (
                  <button data-testid={GAME.startBtn} onClick={startGame} disabled={state.players.length < 2}
                    className="px-6 py-2.5 rounded-full bg-[color:var(--uno-red)] text-white text-sm font-semibold hover:brightness-110 disabled:opacity-40">
                    Start game
                  </button>
                )}
                {!isHost && <div className="text-white/60 text-sm self-center">Waiting for host to start…</div>}
              </div>
            </div>

            <InviteFriends
              roomId={state.room_id}
              playerUserIds={state.players.filter(p => !p.is_bot).map(p => p.user_id)}
            />
          </div>
        </div>
      )}

      {/* Playing */}
      {state.status !== "waiting" && (
        <div className="flex-1 flex flex-col">

          {/* Big turn banner */}
          <div className={`px-4 py-3 sm:py-4 text-center text-sm sm:text-lg font-semibold uppercase tracking-[0.2em] ${
            iAmSwapChooser ? "bg-[color:var(--uno-yellow)] text-black" :
            isMyTurn ? (me?.hand_count === 2 && !me.called_uno ? "bg-[color:var(--uno-red)] text-white" : "bg-[color:var(--uno-green)] text-black") : "bg-white/5 text-white/70"
          }`}>
            {iAmSwapChooser ? "Pick a player to swap hands with" :
             isMyTurn ? (me?.hand_count === 2 && !me.called_uno ? "Tap CALL! before you play your last card" : "Your turn — play or draw") :
             `Waiting for ${currentPlayer?.name || "…"}`}
            {state.pending_draw > 0 && <span className="ml-2 opacity-80">· +{state.pending_draw} incoming</span>}
          </div>

          {/* Opponents row */}
          <div className="px-2 sm:px-6 pt-4 shrink-0">
            <div className="flex flex-wrap gap-3 justify-center max-w-5xl mx-auto">
              {opponents.map(p => {
                const isTurn = state.players[state.current_player_idx]?.user_id === p.user_id;
                return (
                  <div key={p.user_id}
                    className={`relative flex items-center gap-3 rounded-2xl px-3 sm:px-4 py-2 sm:py-2.5 border-2 transition-all ${isTurn ? "border-[color:var(--uno-yellow)] bg-[color:var(--uno-yellow)]/10" : "border-white/10 bg-black/30"} ${drawPulse[p.user_id] ? "!border-[color:var(--uno-red)] scale-105 shadow-lg shadow-[color:var(--uno-red)]/40" : ""}`}>
                    <ReactionBubble reaction={reactions[p.user_id]} />
                    <div className={`relative shrink-0 rounded-full ${isTurn ? "turn-ring" : ""}`}>
                      <img src={p.avatar} alt="" className="w-11 h-11 sm:w-14 sm:h-14 rounded-full border-2 border-white/20"/>
                      <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-black ${p.is_online ? "bg-green-400" : "bg-gray-400"}`}/>
                    </div>
                    <div className="flex flex-col items-start min-w-0 flex-1">
                      <div className="text-sm sm:text-base font-semibold flex items-center gap-1 max-w-[10rem] truncate">
                        {p.name}
                        {p.is_bot && <span className="text-white/40 uppercase text-[9px]">bot</span>}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div className="flex items-center -space-x-3 shrink-0">
                          {Array.from({length: Math.min(p.hand_count, 6)}).map((_, k) => (
                            <div key={k} style={{ zIndex: k }}>
                              <UnoCardBack size="xs" skin={skin}/>
                            </div>
                          ))}
                        </div>
                        <span className="text-xs sm:text-sm text-white font-bold ml-1.5">×{p.hand_count}</span>
                        {p.hand_count === 1 && <span className="text-[10px] uppercase tracking-widest text-red-400 font-bold ml-1">LAST!</span>}
                      </div>
                    </div>
                    {iAmSwapChooser && (
                      <button
                        onClick={() => chooseSwapTarget(p.user_id)}
                        className="ml-1 px-3 py-1 rounded-full text-xs font-semibold bg-[color:var(--uno-yellow)] text-black hover:brightness-110"
                      >Swap</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Center: draw & discard */}
          <div className="flex-1 flex items-center justify-center px-3 relative">
            <div className="flex items-center gap-6 sm:gap-16">
              <button onClick={drawCard} disabled={!isMyTurn} data-testid={GAME.drawBtn}
                className="relative disabled:cursor-not-allowed disabled:opacity-70 hover:enabled:-translate-y-1 transition-transform">
                <div className="hidden sm:block"><UnoCardBack size="lg" skin={skin}/></div>
                <div className="sm:hidden"><UnoCardBack skin={skin}/></div>
                <span className="absolute -bottom-7 left-0 right-0 text-center text-xs sm:text-sm uppercase tracking-widest text-white/80 font-semibold">Draw</span>
                {state.pending_draw > 0 && (
                  <span className="absolute -top-3 -right-3 bg-[color:var(--uno-red)] text-white text-sm font-bold rounded-full w-8 h-8 flex items-center justify-center border-2 border-black">+{state.pending_draw}</span>
                )}
              </button>
              <div className="relative">
                <AnimatePresence mode="popLayout">
                  {topCard && (
                    <motion.div key={topCard.id}
                      initial={{ y: -30, opacity: 0, rotate: -10 }}
                      animate={{ y: 0, opacity: 1, rotate: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ type: "spring", stiffness: 300, damping: 24 }}
                    >
                      <div className="hidden sm:block"><UnoCardView card={topCard} size="lg" skin={skin}/></div>
                      <div className="sm:hidden"><UnoCardView card={topCard} skin={skin}/></div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <span className="absolute -bottom-7 left-0 right-0 flex items-center justify-center gap-2 text-xs sm:text-sm uppercase tracking-widest text-white/80 font-semibold">
                  <span>Color</span>
                  <span className="inline-block w-4 h-4 rounded-full border-2 border-white/40" style={{background: COLOR_HEX[state.current_color] || "#666"}}/>
                </span>
              </div>
            </div>
          </div>

          {/* Bottom: my hand + actions */}
          <div className="pb-3 flex flex-col items-center gap-2 shrink-0">
            <div className="fan flex items-end justify-center gap-1 max-w-full overflow-x-auto pb-1 px-3">
              {me?.hand?.map((card, i, arr) => {
                const playable = isMyTurn && !iAmSwapChooser && canPlayCardClient(card, topCard, state.current_color, state.pending_draw, state.pending_type, rules);
                const rotation = arr.length > 8 ? 0 : ((i - (arr.length - 1) / 2) * 3);
                return (
                  <motion.div key={card.id}
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1, rotate: rotation }}
                    transition={{ delay: i * 0.03, type: "spring", stiffness: 250, damping: 22 }}
                    style={{ zIndex: i }}
                    className="ml-[-10px] sm:ml-[-16px]"
                  >
                    <UnoCardView
                      testId={`play-card-${card.color}-${card.value}`}
                      card={card}
                      skin={skin}
                      playable={playable}
                      onClick={() => playCard(card)}
                    />
                  </motion.div>
                );
              })}
            </div>
            <div className="flex items-center gap-3">
              {me && (
                <div className="flex items-center gap-2 text-xs text-white/60 px-3 py-1 rounded-full bg-white/5">
                  <span className="font-semibold text-white">{me.name}</span>
                  <span>· {me.hand_count} cards</span>
                </div>
              )}
              <button
                data-testid={GAME.unoBtn}
                onClick={callUno}
                disabled={!me || me.hand_count !== 2 || me.called_uno}
                className={`px-5 sm:px-6 py-2 rounded-full font-display text-base sm:text-lg tracking-widest font-bold transition-all ${
                  me?.hand_count === 2 && !me.called_uno
                    ? "bg-[color:var(--uno-red)] text-white uno-pulse shadow-lg shadow-[color:var(--uno-red)]/30"
                    : me?.called_uno
                    ? "bg-[color:var(--uno-green)]/20 text-[color:var(--uno-green)] border border-[color:var(--uno-green)]/40"
                    : "bg-white/5 text-white/30"
                } disabled:cursor-not-allowed`}
              >{me?.called_uno ? "✓ Called" : "Call!"}</button>
            </div>
          </div>

          {/* Finished — WinAnimation with confetti */}
          <AnimatePresence>
            {state.status === "finished" && (() => {
              const winner = state.players.find(p => p.user_id === state.winner);
              return (
                <WinAnimation
                  key="win"
                  winnerName={winner?.name || "Someone"}
                  winnerAvatar={winner?.avatar}
                  isMe={state.winner === user?.user_id}
                  subtitle="Coins were added to your balance."
                  onContinue={() => nav("/lobby", { replace: true })}
                  primaryLabel="Play again"
                  onPrimary={restart}
                  canPrimary={isHost}
                />
              );
            })()}
          </AnimatePresence>
        </div>
      )}

      {/* Global deal animation (fires once when game starts) */}
      <DealAnimation
        active={dealActive}
        players={state.players}
        myUserId={user?.user_id}
        cardsPerPlayer={7}
        onDone={() => setDealActive(false)}
        variant="uno"
      />

      {/* Card-play flight layer */}
      <PlayFlightLayer
        plays={playFlights}
        players={state.players}
        myUserId={user?.user_id}
        renderCard={(card) => <UnoCardView card={card} size="lg" skin={skin}/>}
        onFinished={dequeueFlight}
      />

      {/* Self-reaction bubble (floats just left of the react button) */}
      <AnimatePresence>
        {reactions[user?.user_id] && (
          <motion.div
            key={reactions[user?.user_id].id}
            initial={{ opacity: 0, x: 20, scale: 0.6 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 320, damping: 20 }}
            className="fixed top-1/2 -translate-y-1/2 right-16 sm:right-20 z-40 pointer-events-none max-w-[60vw]"
          >
            <div className="bg-white text-black rounded-2xl px-3 py-2 shadow-xl flex items-center gap-1.5 border-2 border-[color:var(--uno-yellow)]">
              <motion.span
                animate={{ rotate: [0, -18, 18, 0], scale: [1, 1.35, 1.1, 1] }}
                transition={{ duration: 0.75, ease: "easeOut" }}
                className="text-xl leading-none"
              >{reactions[user?.user_id].emoji}</motion.span>
              <span className="text-sm font-bold">{reactions[user?.user_id].text}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating react button — vertically centered on right edge, doesn't cover gameplay */}
      <div className="fixed top-1/2 -translate-y-1/2 right-3 sm:right-4 z-30">
        <QuickChat send={send} disabled={state.status !== "playing"} direction="left" />
      </div>

      {/* Color chooser modal */}
      <AnimatePresence>
        {pickingColorFor && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="glass rounded-2xl p-6 sm:p-8">
              <div className="text-center text-xs uppercase tracking-[0.3em] text-white/60 mb-4 sm:mb-6">Choose a color</div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {[["red", GAME.colorChooserRed],["yellow", GAME.colorChooserYellow],["green", GAME.colorChooserGreen],["blue", GAME.colorChooserBlue]].map(([c, tid]) => (
                  <button key={c} data-testid={tid} onClick={() => chooseColor(c)}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border-2 border-white/20 hover:scale-105 transition-transform active:scale-95"
                    style={{background: COLOR_HEX[c]}}
                    aria-label={`Choose ${c}`}
                  />
                ))}
              </div>
              <button onClick={() => setPickingColorFor(null)} className="mt-5 w-full text-center text-xs uppercase tracking-widest text-white/50 hover:text-white">Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {chatOpen && (
          <motion.aside initial={{x: 380}} animate={{x:0}} exit={{x: 380}} transition={{type:"spring", stiffness:220, damping: 26}}
            className="absolute top-0 right-0 bottom-0 w-full sm:w-[360px] max-w-full z-30 flex flex-col glass border-l border-white/10">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.3em] text-white/60">Table chat</div>
              <button onClick={() => setChatOpen(false)} className="text-white/50 hover:text-white text-sm">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chat.map((m, i) => (
                <div key={i} className="flex items-start gap-2">
                  <img src={m.avatar} alt="" className="w-8 h-8 rounded-full mt-0.5 border border-white/10"/>
                  <div>
                    <div className="text-xs text-white/60">{m.name}</div>
                    <div className="text-sm bg-white/5 rounded-xl px-3 py-1.5 inline-block">{m.text}</div>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef}/>
            </div>
            <div className="p-3 border-t border-white/10 flex gap-2">
              <input
                data-testid={GAME.chatInput}
                value={chatText}
                onChange={e => setChatText(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendChat()}
                placeholder="Say something…"
                className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-[color:var(--uno-yellow)]"
              />
              <button data-testid={GAME.chatSendBtn} onClick={sendChat} className="px-4 py-2 rounded-full bg-white text-black text-sm font-semibold">Send</button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </div>
  );
}
